import { useEffect, useState } from 'react';
import type { PriceType } from '../types/PriceType';
import { getPriceTypes } from '../api/priceTypes';

// Module-level cache shared by every hook caller for the lifetime of the page.
// `inflight` deduplicates concurrent fetches; cleared on failure so the next
// mount retries. Fetched LAZILY — only when this hook first runs; never part of
// any login/startup preload. Cleared on logout via clearPriceTypesCache() —
// next mount re-fetches. Mirrors useCrcies.
let cache: PriceType[] | null = null;
let inflight: Promise<PriceType[]> | null = null;

function loadPriceTypes(): Promise<PriceType[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getPriceTypes()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearPriceTypesCache(): void {
  cache = null;
  inflight = null;
}

interface UsePriceTypesResult {
  data: PriceType[] | null;
  loading: boolean;
  error: string | null;
}

export function usePriceTypes(): UsePriceTypesResult {
  const [state, setState] = useState<UsePriceTypesResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadPriceTypes()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
